import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Confirm,
  Dimmer,
  Form,
  Icon,
  Loader,
  Message,
  Segment,
  Table,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { required } from '@mhub/web-validations';
import { flashErrorMessage, flashSuccessMessage } from '../actions/flash';
import { fetchM } from '../actions/fetchM';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';
import AppForm from './AppForm';

const DragHandle = SortableHandle(() => (
  <Icon name="bars" style={{ cursor: 'grab' }} />
));

const SortableItem = SortableElement(({ reason, onEdit, onDelete, onNameChange, onLabelChange, isEditing, validation, t, isHQ }) => (
  <Table.Row>
    <Table.Cell width={1} textAlign="center">
      {isHQ && <DragHandle />}
    </Table.Cell>
    <Table.Cell width={6} textAlign="center">
      {isEditing ? (
        <AppForm.Input
          validateLabel={t('Reason Name')}
          name={`reason_${reason.id}`}
          value={reason.label}
          validation={[required]}
          onChange={(e, { value }) => onLabelChange(reason.id, value)}
          ref={validation[`reason_${reason.id}`]}
        />
      ) : (
        reason.label
      )}
    </Table.Cell>
    <Table.Cell width={3} textAlign="center">
      {reason.isDefault ? (
        <Icon name="check" color="green" />
      ) : (
        <Icon name="times" color="grey" />
      )}
    </Table.Cell>
    <Table.Cell width={3} textAlign="center">
      {isHQ && (
        <React.Fragment>
          {reason.isDefault || reason.isInUse ? (
            <Icon 
              name="lock" 
              color="grey" 
              title={reason.isDefault 
                ? t('Default reason cannot be modified') 
                : t('Cannot delete, this reason is currently being used by delisted units')
              }
            />
          ) : (
            <React.Fragment>
              {isEditing ? (
                <Button
                  size="mini"
                  color="green"
                  icon="save"
                  content={t('Save')}
                  onClick={() => onEdit(reason.id, true)}
                />
              ) : (
                <React.Fragment>
                  <Button
                    size="mini"
                    icon="edit"
                    content={t('Edit')}
                    onClick={() => onEdit(reason.id, false)}
                  />
                  <Button
                    size="mini"
                    color="red"
                    icon="trash"
                    content={t('Delete')}
                    onClick={() => onDelete(reason.id)}
                  />
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Table.Cell>
  </Table.Row>
));

const SortableList = SortableContainer(({ reasons, onEdit, onDelete, onNameChange, onLabelChange, editingReasonID, validation, t, isHQ }) => (
  <Table.Body>
    {reasons.map((reason, index) => (
      <SortableItem
        key={reason.id}
        index={index}
        reason={reason}
        onEdit={onEdit}
        onDelete={onDelete}
        onNameChange={onNameChange}
        onLabelChange={onLabelChange}
        isEditing={editingReasonID === reason.id}
        validation={validation}
        t={t}
        isHQ={isHQ}
        disabled={!isHQ}
      />
    ))}
  </Table.Body>
));

class DelistReasonsList extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    isHeadquarters: PropTypes.bool,
    isSuperAdmin: PropTypes.bool,
    isAdminBasic: PropTypes.bool,
    t: PropTypes.func,
  }
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      reasons: [],
      editingReasonID: null,
      deleteConfirmOpen: false,
      deleteReasonID: null,
      newReasonName: '',
      showAddForm: false,
      token: null,
    };
    this.validation = {};
    this.newReasonValidation = createRef();
  }
  componentDidMount() {
    this.fetchDelistReasons();
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async fetchDelistReasons() {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    let res;
    try {
      res = await store.dispatch(fetchM('/settings/delist_reasons/', {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const token = res.headers.get('X-Csrf-Token');
      const reasons = await res.json();
      // Sort by position
      reasons.sort((a, b) => a.position - b.position);
      await this.setComponentState({
        token,
        reasons: reasons || [],
      });
    }
    await this.setLoading(false);
  }
  handleAddNew = () => {
    this.setState({ showAddForm: true, newReasonName: '' });
  }
  handleCancelAdd = () => {
    this.setState({ showAddForm: false, newReasonName: '', editingReasonID: null });
  }
  handleSaveNew = async () => {
    const { newReasonName, token } = this.state;
    const { store } = this.context;
    const { t } = this.props;

    // Validate
    const validations = await AppForm.validate({ newReason: this.newReasonValidation }, { newReason: newReasonName });
    if (validations.some(v => !v.isValid)) {
      return;
    }

    await this.setLoading(true);
    let res;
    try {
      // Generate label from name (convert snake_case/lowercase to Title Case)
      const label = newReasonName.trim();
      const name = label.toLowerCase().replace(/\s+/g, '_');
      
      res = await store.dispatch(fetchM('/settings/delist_reasons/', {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': token,
        },
        body: JSON.stringify({
          name: name,
          label: label,
        }),
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      await store.dispatch(flashSuccessMessage({ message: t('Delist reason added successfully') }));
      this.setState({ showAddForm: false, newReasonName: '' });
      await this.fetchDelistReasons();
    }
    await this.setLoading(false);
  }
  handleEdit = async (reasonID, save) => {
    const { reasons, token } = this.state;
    const { store } = this.context;
    const { t } = this.props;

    if (save) {
      // Validate
      const reason = reasons.find(r => r.id === reasonID);
      const validations = await AppForm.validate(
        { [`reason_${reasonID}`]: this.validation[`reason_${reasonID}`] },
        { [`reason_${reasonID}`]: reason.label }
      );
      if (validations.some(v => !v.isValid)) {
        return;
      }

      await this.setLoading(true);
      let res;
      try {
        // Generate name from label
        const name = reason.label.toLowerCase().replace(/\s+/g, '_');
        
        res = await store.dispatch(fetchM(`/settings/delist_reasons/${reasonID}/`, {
          credentials: 'include',
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Csrf-Token': token,
          },
          body: JSON.stringify({
            name: name,
            label: reason.label,
          }),
        }));
      } catch (e) {
        Bugsnag.notifyException(e);
        let errorMessage = t('An error occurred', { ns: TRANSLATION_FILES.translation });
        if (e.res) {
          try {
            errorMessage = await e.res.text();
          } catch (textErr) {
            // If can't read the error message, use the default
          }
        }
        await store.dispatch(flashErrorMessage({ header: 'Unable to Proceed', message: errorMessage }));
        await this.setLoading(false);
        return;
      }
      if (!res.ok) {
        const message = await res.text();
        await store.dispatch(flashErrorMessage({ header: 'Unable to Proceed', message }));
      } else {
        await store.dispatch(flashSuccessMessage({ message: t('Delist reason updated successfully') }));
        this.setState({ editingReasonID: null });
        await this.fetchDelistReasons();
      }
      await this.setLoading(false);
    } else {
      this.setState({ editingReasonID: reasonID });
    }
  }
  handleDelete = (reasonID) => {
    this.setState({ deleteConfirmOpen: true, deleteReasonID: reasonID });
  }
  handleConfirmDelete = async () => {
    const { deleteReasonID, token } = this.state;
    const { store } = this.context;
    const { t } = this.props;

    await this.setLoading(true);
    this.setState({ deleteConfirmOpen: false });

    let res;
    try {
      res = await store.dispatch(fetchM(`/settings/delist_reasons/${deleteReasonID}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': token,
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      let errorMessage = t('An error occurred', { ns: TRANSLATION_FILES.translation });
      if (e.res) {
        try {
          errorMessage = await e.res.text();
        } catch (textErr) {
          // If can't read the error message, use the default
        }
      }
      await store.dispatch(flashErrorMessage({ header: 'Unable to Proceed', message: errorMessage }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ header: 'Unable to Proceed', message }));
    } else {
      await store.dispatch(flashSuccessMessage({ message: t('Delist reason deleted successfully') }));
      await this.fetchDelistReasons();
    }
    await this.setLoading(false);
  }
  handleCancelDelete = () => {
    this.setState({ deleteConfirmOpen: false, deleteReasonID: null });
  }
  handleNameChange = (reasonID, value) => {
    const { reasons } = this.state;
    const index = reasons.findIndex(r => r.id === reasonID);
    if (index >= 0) {
      reasons[index].name = value;
      this.setState({ reasons });
    }
  }
  handleLabelChange = (reasonID, value) => {
    const { reasons } = this.state;
    const index = reasons.findIndex(r => r.id === reasonID);
    if (index >= 0) {
      reasons[index].label = value;
      this.setState({ reasons });
    }
  }
  handleSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) {
      return;
    }

    const { reasons, token } = this.state;
    const { store } = this.context;
    const { t } = this.props;

    // Reorder array
    const reorderedReasons = [...reasons];
    const [removed] = reorderedReasons.splice(oldIndex, 1);
    reorderedReasons.splice(newIndex, 0, removed);

    // Update positions
    const updates = reorderedReasons.map((reason, index) => ({
      id: reason.id,
      position: index + 1,
    }));

    this.setState({ reasons: reorderedReasons });

    await this.setLoading(true);
    let res;
    try {
      res = await store.dispatch(fetchM('/settings/delist_reasons/reorder/', {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': token,
        },
        body: JSON.stringify(updates),
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred', { ns: TRANSLATION_FILES.translation }) }));
      // Revert order on error
      await this.fetchDelistReasons();
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
      // Revert order on error
      await this.fetchDelistReasons();
    } else {
      await store.dispatch(flashSuccessMessage({ message: t('Order updated successfully') }));
    }
    await this.setLoading(false);
  }
  render() {
    const { t, isHeadquarters, isSuperAdmin, isAdminBasic } = this.props;
    const { reasons, loading, showAddForm, newReasonName, editingReasonID, deleteConfirmOpen } = this.state;
    const isHQ = isHeadquarters || isSuperAdmin || isAdminBasic;

    // Add validation refs
    reasons.forEach((reason) => {
      if (!this.validation[`reason_${reason.id}`]) {
        this.validation[`reason_${reason.id}`] = createRef();
      }
    });

    return (
      <div>
        <Dimmer
          inverted style={{ zIndex: 20 }}
          active={loading && reasons.length > 0}
        >
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>

        <Message info>
          <Message.Header>{t('Delist Reasons Management')}</Message.Header>
          <p>{t('Manage the list of reasons that can be selected when delisting a unit. Default reasons cannot be edited or deleted.')}</p>
          <p>{t('Reasons that are currently in use by delisted units cannot be deleted.')}</p>
          {!isHQ && <p><em>{t('Only HQ roles can manage delist reasons.')}</em></p>}
        </Message>

        {isHQ && (
          <Segment>
            {showAddForm ? (
              <Form>
                <Form.Group>
                  <Form.Field width={10}>
                    <AppForm.Input
                      validateLabel={t('New Reason Name')}
                      name="newReason"
                      value={newReasonName}
                      validation={[required]}
                      onChange={(e, { value }) => this.setState({ newReasonName: value })}
                      ref={this.newReasonValidation}
                      placeholder={t('Enter reason name...')}
                    />
                  </Form.Field>
                  <Form.Field width={3}>
                    <Button
                      color="green"
                      icon="check"
                      content={t('Save')}
                      onClick={this.handleSaveNew}
                      fluid
                    />
                  </Form.Field>
                  <Form.Field width={3}>
                    <Button
                      icon="cancel"
                      content={t('Cancel')}
                      onClick={this.handleCancelAdd}
                      fluid
                    />
                  </Form.Field>
                </Form.Group>
              </Form>
            ) : (
              <Button
                color="blue"
                icon="plus"
                content={t('Add New Reason')}
                onClick={this.handleAddNew}
              />
            )}
          </Segment>
        )}

        <Table striped unstackable celled>
          <Table.Header fullWidth>
            <Table.Row>
              <Table.HeaderCell width={1} textAlign="center">{isHQ ? t('Order') : ''}</Table.HeaderCell>
              <Table.HeaderCell width={6} textAlign="center">{t('Reason Name')}</Table.HeaderCell>
              <Table.HeaderCell width={3} textAlign="center">{t('Default')}</Table.HeaderCell>
              <Table.HeaderCell width={3} textAlign="center">{t('Actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          {reasons.length > 0 ? (
            <SortableList
              reasons={reasons}
              onSortEnd={this.handleSortEnd}
              useDragHandle
              onEdit={this.handleEdit}
              onDelete={this.handleDelete}
              onNameChange={this.handleNameChange}
              onLabelChange={this.handleLabelChange}
              editingReasonID={editingReasonID}
              validation={this.validation}
              t={t}
              isHQ={isHQ}
              lockAxis="y"
              helperClass="sortable-helper"
            />
          ) : (
            <Table.Body>
              <Table.Row>
                <Table.Cell colSpan={4} textAlign="center">
                  {loading ? (
                    <Loader active inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
                  ) : (
                    <p>{t('No delist reasons found')}.</p>
                  )}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          )}
        </Table>

        <Confirm
          open={deleteConfirmOpen}
          header={t('Delete Delist Reason')}
          content={t('Are you sure you want to delete this delist reason? This action cannot be undone.')}
          cancelButton={t('Cancel')}
          confirmButton={t('Delete')}
          onCancel={this.handleCancelDelete}
          onConfirm={this.handleConfirmDelete}
        />
      </div>
    );
  }
}

export default withTranslation(namespaces, { withRef: true })(DelistReasonsList);



// WEBPACK FOOTER //
// ./src/components/DelistReasonsList.jsx